import React, { useState, useEffect, useContext } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Snackbar, Alert, MenuItem, FormControl, InputLabel, Select,
  useTheme, useMediaQuery, Box
} from "@mui/material";
import { ThemeContext } from "../../../../../../../../context/ThemeContextProvider";
import {  fetchCaptains } from "../../../../../../../../services/pos/Tables";
import { useOrder } from "../../../../../../../../context/OrderCreator/TablesContext/OrderContext";

const EditOrderForm = ({ open, onClose, initialData }) => {
  const { mode } = useContext(ThemeContext);
  const muiTheme = useTheme();

  // responsive breakpoints
  const isSmallTablet = useMediaQuery("(min-width:600px) and (max-width:768px)");
  const isMediumTablet = useMediaQuery("(min-width:769px) and (max-width:900px)");
  const isPortrait = useMediaQuery("(orientation: portrait)");

  const dialogFullScreen = isSmallTablet && isPortrait; // full screen on small tablets in portrait
  const responsivePadding = isSmallTablet ? 1 : isMediumTablet ? 1.5 : 2;
  const inputFontSize = isSmallTablet ? "13px" : "14px";
  const buttonHeight = isSmallTablet ? 40 : 44;

  const [form, setForm] = useState({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [captains, setCaptains] = useState([]);
  const { updateOrderData } = useOrder();


  useEffect(() => {
    if (initialData) setForm(initialData);
  }, [initialData]);

  useEffect(() => {
    if (open) {
      fetchCaptains()
        .then((data) => setCaptains(data))
        .catch((err) => setCaptains([]));
    }
  }, [open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (!form.order_id) throw new Error("❌ رقم الأوردر غير موجود");

      await updateOrderData(form.order_id, {
        captain_order_id: form.captain_order_id,
        table_id: form.table_id || "",
        customer_id: form.customer_id || form.customer?.id || "",
      });

      setSuccess(true);
      onClose(true);
    } catch (err) {
      setError(err.message || "❌ فشل في التعديل");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        fullWidth
        fullScreen={dialogFullScreen}
        PaperProps={{
          sx: {
            bgcolor: mode === "dark" ? "#232334" : "#fff",
            color: mode === "dark" ? "#fff" : "#232334",
            borderRadius: dialogFullScreen ? 0 : 3,
            height: dialogFullScreen ? "100dvh" : "auto",
            boxSizing: "border-box",
          },
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: isSmallTablet ? 18 : 22,
              textAlign: "center",
              color: "#EA7C69",
              bgcolor: mode === "dark" ? "#181824" : "#fff",
              borderRadius: dialogFullScreen ? 0 : "16px 16px 0 0",
              py: 1.25,
            }}
          >
            تعديل بيانات الكابتن
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              pb: 2,
              px: { xs: 2, sm: 3 },
              pt: 2,
            }}
          >
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ bgcolor: mode === "dark" ? "#181824" : "#fff", borderRadius: 2 }}>
                  <InputLabel sx={{ color: mode === "dark" ? "#aaa" : undefined }}>اختر الكابتن</InputLabel>
                  <Select
                    label="اختر الكابتن"
                    name="captain_order_id"
                    value={form.captain_order_id || ""}
                    onChange={handleChange}
                    sx={{
                      color: mode === "dark" ? "#fff" : "#232334",
                      fontSize: inputFontSize,
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: mode === "dark" ? "#444" : "#ccc",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: mode === "dark" ? "#232334" : "#fff",
                          color: mode === "dark" ? "#fff" : "#232334",
                        },
                      },
                    }}
                  >
                    {captains.map((captain) => (
                      <MenuItem key={captain.id} value={captain.id} sx={{ fontSize: inputFontSize }}>
                        {captain.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* optional: table selector for responsive layouts */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="رقم الطاولة"
                  name="table_id"
                  value={form.table_id || ""}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{ style: { fontSize: inputFontSize } }}
                  sx={{
                    bgcolor: mode === "dark" ? "#181824" : "#fff",
                    borderRadius: 2,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="معرف العميل (اختياري)"
                  name="customer_id"
                  value={form.customer_id || ""}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{ style: { fontSize: inputFontSize } }}
                  sx={{
                    bgcolor: mode === "dark" ? "#181824" : "#fff",
                    borderRadius: 2,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ bgcolor: mode === "dark" ? "#232334" : "#fff", borderRadius: dialogFullScreen ? 0 : "0 0 16px 16px", px: 3, py: 2 }}>
            <Button
              onClick={() => onClose(false)}
              variant="outlined"
              color="secondary"
              sx={{
                minWidth: 100,
                borderColor: mode === "dark" ? "#888" : undefined,
                color: mode === "dark" ? "#fff" : undefined,
                height: buttonHeight,
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              type="submit"
              sx={{
                bgcolor: "#EA7C69",
                color: "#fff",
                minWidth: 120,
                fontWeight: 700,
                height: buttonHeight,
                "&:hover": { bgcolor: "#d45d4c" },
              }}
            >
              حفظ التعديلات
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError("")}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success">✅ تم التعديل بنجاح</Alert>
      </Snackbar>
    </>
  );
};

export default EditOrderForm;
